import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAnalyticsConsent } from '@/lib/cookie-consent';
import { analytics } from '@/services/analytics';
import { resolvePublicSeo } from '@/services/seo.service';

function seoPathFromLocation(pathname: string): string {
  return pathname === '/' ? '/' : pathname.replace(/\/+$/, '') || '/';
}

/** Regista page_view (GA4 + MySQL) em cada navegação, com título já atualizado. */
export function AnalyticsRouteTracker() {
  const location = useLocation();
  const lastPath = useRef('');
  const lastTitle = useRef('');

  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;

    let cancelled = false;
    let sent = false;
    const titleAtNav = document.title;

    const send = (title: string) => {
      if (cancelled || sent) return;
      if (!hasAnalyticsConsent()) {
        lastTitle.current = title || document.title;
        return;
      }
      sent = true;
      lastTitle.current = title || document.title;
      void analytics.pageView(path, title || document.title);
    };

    // Título canónico via SEO (evita document.title da página anterior).
    void resolvePublicSeo(seoPathFromLocation(location.pathname)).then((seo) => {
      if (cancelled || sent) return;
      if (seo?.title?.trim()) {
        send(seo.title.trim());
      }
    });

    const observer = new MutationObserver(() => {
      if (document.title && document.title !== titleAtNav) {
        send(document.title);
        observer.disconnect();
      }
    });
    const titleEl = document.querySelector('title');
    if (titleEl) {
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }

    // Fallback se SEO falhar e o <title> não mudar a tempo.
    const fallbackTimer = window.setTimeout(() => {
      send(document.title);
    }, 1200);

    return () => {
      cancelled = true;
      observer.disconnect();
      window.clearTimeout(fallbackTimer);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onConsent = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      if (detail !== 'accepted') return;
      const path = location.pathname + location.search;
      void analytics.pageView(path, lastTitle.current || document.title);
    };
    window.addEventListener('xat-cookie-consent-changed', onConsent);
    return () => window.removeEventListener('xat-cookie-consent-changed', onConsent);
  }, [location.pathname, location.search]);

  return null;
}
